import React, { FC, useEffect, useRef, useState } from "react";
import clsx from "clsx";
import List from "@mui/material/List";
import ListItemText from "@mui/material/ListItemText";
import ListItem from "@mui/material/ListItem";
import Collapse from "@mui/material/Collapse";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { ThemeProvider, styled } from "@mui/material";
import { connect, ConnectedProps } from "react-redux";
import HoverMenu from "../HoverMenu/HoverMenu";
import IOPDrawerItem from "../IOPDrawerItem/IOPDrawerItem";
import useHrefBuilder from "../../hooks/UseHrefBuilder";
import { setActiveMenuItem } from "../../redux/actions";
import theme from "../../theme";

const mapState = (state: {
    activeMenuItem: any;
    activeMenuTrail: string[];
}) => ({
    activeMenuItem: state.activeMenuItem,
    activeMenuTrail: state.activeMenuTrail,
});

const connector = connect(mapState, {});

type PropsFromRedux = ConnectedProps<typeof connector>;

type Props = PropsFromRedux & {
    drawerItem: MainMenuItemInterface;
    drawerOpen: boolean;
    handleDrawerToggle: (open: boolean) => void;
    depth: number;
    unreadCount: number; 
};

const IOPDrawerWrapper: FC<Props> = ({
    drawerItem,
    depth,
    drawerOpen,
    handleDrawerToggle,
    activeMenuItem,
    activeMenuTrail,
    unreadCount,
}) => {

    const [open, setOpen] = useState(false);
    const hrefBuilder = useHrefBuilder();
    const href = hrefBuilder(drawerItem);

    const inActiveTrail = activeMenuTrail.includes(drawerItem.id);

    const isActive = activeMenuItem
        ? activeMenuItem === drawerItem.id
        : window.location.pathname === href;

    const [showHoverMenu, setShowHoverMenu] = useState<boolean>(false);
    const listItemTextRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (isActive) {
            setActiveMenuItem(drawerItem.id);
        }
    }, [drawerItem.id, isActive]);

    useEffect(() => {
        !drawerOpen && setOpen(false);
        drawerOpen && setShowHoverMenu(false);
    }, [drawerOpen]);

    const toggleCategory = () => setOpen(!open);

    const nestedDrawerItems = drawerItem.children?.map(
        (item: MainMenuItemInterface) => (
            <IOPDrawerWrapper
                key={item.id}
                drawerItem={item}
                depth={depth + 1}
                drawerOpen={drawerOpen}
                handleDrawerToggle={handleDrawerToggle}
                activeMenuItem={activeMenuItem}
                activeMenuTrail={activeMenuTrail}
                unreadCount={unreadCount} 
            />
        )
    );

    useEffect(() => {
        if (!listItemTextRef.current) return;
        listItemTextRef.current.style.marginLeft = `${depth * parseInt(theme.spacing(2))}px`;
    }, [depth]);

    const renderItem = () => {
        return (
            <StyledListItem
                className={clsx({
                    isActive,
                    inActiveTrail: isActive ? false : inActiveTrail,
                })}
                onMouseEnter={() => setShowHoverMenu(!drawerOpen)}
                onMouseLeave={() => setShowHoverMenu(false)}
            >
                <StyledListItemText
                    className="list-item-text"
                    ref={listItemTextRef}
                >
                    <IOPDrawerItem
                        item={drawerItem}
                        unreadCount={unreadCount} 
                        isActive={isActive}
                        handleDrawerToggle={handleDrawerToggle}
                        href={href}
                        toggleCategory={toggleCategory}
                    />
                </StyledListItemText>

                {"category" === drawerItem.type &&
                    drawerOpen &&
                    (open ? (
                        <ExpandLess
                            style={{
                                cursor: "pointer",
                                paddingRight: "16px",
                                paddingTop: "15px",
                                paddingBottom: "15px"
                            }}
                            onClick={toggleCategory}
                        />
                    ) : (
                        <ExpandMore
                            style={{
                                cursor: "pointer",
                                paddingRight: "16px",
                                paddingTop: "15px",
                                paddingBottom: "15px"
                            }}
                            onClick={toggleCategory}
                        />
                    ))}

                {showHoverMenu && (
                    <HoverMenu
                        category={drawerItem.children?.length > 0 ? drawerItem.label : null}
                        items={
                            drawerItem.children?.length > 0
                                ? drawerItem.children
                                : [drawerItem]
                        }
                        showHoverMenu={showHoverMenu}
                        indent={73}
                        parentRef={listItemTextRef}
                        handleDrawerToggle={handleDrawerToggle}
                        toggleCategory={toggleCategory}
                    />
                )}
            </StyledListItem>
        );
    };

    return (
        <ThemeProvider theme={theme}>
            <div>
                {renderItem()}

                {"category" === drawerItem.type ? (
                    <Collapse
                        in={open}
                        timeout="auto"
                    >
                        <List disablePadding={true}>{nestedDrawerItems}</List>
                    </Collapse>
                ) : (
                    <List disablePadding={true}>{nestedDrawerItems}</List>
                )}
            </div>
        </ThemeProvider>
    );
};

const StyledListItem = styled(ListItem)(({ theme }) => ({
    color: theme.palette.primary.main,
    paddingLeft: "0",
    paddingRight: "0",
    paddingBottom: "0",
    paddingTop: "0",
    "&.isActive": {
        backgroundColor: theme.palette.secondary.main,
        "& .list-item-text a": { color: "#fff" },
    },
    "&.inActiveTrail": {
        backgroundColor: theme.palette.secondary.light,
        "& .list-item-text a": { color: "#fff" },
    },
    "&:hover": {
        backgroundColor: theme.palette.primary.main,
        color: "#fff",
        "& .list-item-text a": {
            color: "#fff",
        },
    },
}));

const StyledListItemText = styled(ListItemText)(({ theme }) => ({
    marginBottom: "0",
    marginLeft: "0",
    marginTop: "0",
}));

export default connector(IOPDrawerWrapper);